import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, Copy, Check, ShieldCheck, Lock, CheckCircle2, AlertCircle, Clock, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtNaira, fmtDate, whatsappLink, isIOwe, publicUrl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function ConfirmPaymentSheet({ open, onOpenChange, debt, payment, onRequested }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const isPlus = user?.subscription_status === "plus";

  const status = payment?.confirmation_status || "none";
  const info = payment?.confirmation || {};
  const revised = info.revised;
  const iOwe = isIOwe(debt);

  useEffect(() => {
    if (open && status === "pending" && info.path) {
      setLink(publicUrl(info.path));
    }
  }, [open, status, info.path]);

  // Safety net for the Vaul drawer / Radix interplay that can leave the page
  // with document.body { pointer-events: none } after closing.
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => { document.body.style.pointerEvents = ""; }, 0);
      return () => clearTimeout(t);
    }
  }, [open]);
  useEffect(() => () => { document.body.style.pointerEvents = ""; }, []);

  const title =
    status === "confirmed" ? "Repayment confirmed"
      : status === "questioned" ? "Repayment questioned"
        : status === "pending" ? "Awaiting confirmation"
          : `Ask ${debt?.person_name} to confirm`;

  const amountStr = payment ? fmtNaira(payment.amount, debt?.currency) : "";
  const message = link
    ? (iOwe
      ? `Hi ${debt?.person_name}, I've recorded a ${amountStr} repayment towards what I owe you on Debii.${revised ? " I've updated it." : ""} Please confirm you received it: ${link}`
      : `Hi ${debt?.person_name}, I've recorded your ${amountStr} repayment on Debii.${revised ? " I've updated it." : ""} Please confirm the details: ${link}`)
    : "";

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/debts/${debt.id}/payments/${payment.id}/confirmation`);
      setLink(publicUrl(data.path));
      onRequested?.();
    } catch (e) {
      const err = apiError(e);
      if (err === "plus_required") toast.error("Requesting confirmation is a Debii Plus feature.");
      else if (err === "already_confirmed") toast.error("This repayment is already confirmed.");
      else toast.error(err);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(message);
    setCopied(true);
    toast.success("Message copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const close = () => { setLink(null); onOpenChange(false); };

  const sendEmail = async () => {
    const to = emailTo.trim();
    if (!to || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setEmailSending(true);
    try {
      await api.post(`/debts/${debt.id}/payments/${payment.id}/confirmation/email`, { email: to });
      toast.success(`Request emailed to ${to}`);
      setEmailTo("");
      onRequested?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEmailSending(false);
    }
  };

  const ShareView = (
    <>
      {status === "pending" && (
        <div className="flex items-center gap-2 rounded-xl bg-secondary px-3 py-2 text-sm font-medium text-slate-600">
          <Clock className="h-4 w-4" /> Awaiting {debt?.person_name}'s confirmation{revised ? " (updated)" : ""}
        </div>
      )}
      <div data-testid="confirm-link-box" className="rounded-2xl bg-secondary p-4 text-sm break-all text-foreground">{link}</div>
      <div className="rounded-2xl border border-border p-4 text-sm leading-relaxed text-muted-foreground">{message}</div>
      <div className="space-y-2">
        <Button
          data-testid="confirm-whatsapp-button"
          onClick={() => window.open(whatsappLink(debt.person_phone, message), "_blank")}
          className="w-full h-12 rounded-xl text-base bg-[#25D366] hover:bg-[#20bd5a] text-white"
        >
          <MessageCircle className="h-5 w-5" /> Share via WhatsApp
        </Button>
        <Button data-testid="confirm-copy-button" variant="outline" onClick={copy} className="w-full h-11 rounded-xl">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy message
        </Button>
        <div className="pt-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground pb-1.5">
            <Mail className="h-3.5 w-3.5" /> Or email the request directly
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="confirm-email-input"
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: "center", behavior: "smooth" }), 300)}
              placeholder={`${debt?.person_name || "Recipient"}'s email (optional)`}
              className="h-11 rounded-xl"
            />
            <Button data-testid="confirm-email-send" variant="outline" onClick={sendEmail} disabled={emailSending || !emailTo.trim()} className="h-11 rounded-xl shrink-0">
              {emailSending ? "Sending…" : "Send"}
            </Button>
          </div>
        </div>
        <Button variant="ghost" onClick={close} className="w-full h-11 rounded-xl">Done</Button>
      </div>
    </>
  );

  const renderBody = () => {
    if (!isPlus) {
      return (
        <div className="text-center py-4 space-y-4">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center">
            <Lock className="h-7 w-7 text-primary" />
          </div>
          <p className="text-sm text-muted-foreground">
            Asking someone to confirm a repayment is part of <span className="font-semibold text-foreground">Debii Plus</span>. Confirming is always free.
          </p>
          <Button data-testid="confirm-upgrade-button" onClick={() => { close(); navigate("/upgrade"); }} className="w-full h-12 rounded-xl">
            Upgrade to Debii Plus
          </Button>
        </div>
      );
    }
    if (status === "confirmed") {
      return (
        <div data-testid="confirm-confirmed-view" className="text-center py-4 space-y-3">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="h-7 w-7 text-emerald-700" />
          </div>
          <p className="font-display text-lg font-bold text-emerald-800">Confirmed ✓</p>
          <p className="text-sm text-muted-foreground">
            {debt.person_name} confirmed the {amountStr} repayment{info.responded_at ? ` on ${fmtDate(info.responded_at)}` : ""}.
          </p>
          <Button variant="ghost" onClick={close} className="w-full h-11 rounded-xl">Done</Button>
        </div>
      );
    }
    if (status === "questioned" && !link) {
      return (
        <div data-testid="confirm-questioned-view" className="space-y-4">
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 space-y-1.5">
            <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
              <AlertCircle className="h-4 w-4" /> {debt.person_name} questioned this repayment
            </div>
            {info.dispute_reason_label && <p className="text-sm text-amber-900">Flagged: {info.dispute_reason_label}</p>}
            {info.dispute_comment && <p className="text-sm text-muted-foreground italic">“{info.dispute_comment}”</p>}
          </div>
          <p className="text-sm text-muted-foreground">
            Review the concern together, then send an updated confirmation request. {debt.person_name} will see it was updated after being questioned.
          </p>
          <Button data-testid="confirm-resend-button" disabled={loading} onClick={generate} className="w-full h-12 rounded-xl text-base">
            {loading ? "Creating link…" : "Send updated request"}
          </Button>
        </div>
      );
    }
    if (link) return ShareView;
    if (status === "pending") {
      return (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-2xl bg-secondary p-4">
            <Clock className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">You've asked {debt?.person_name} to confirm this repayment. Get the link below to re-share it.</p>
          </div>
          <Button data-testid="confirm-reshare-button" disabled={loading} onClick={generate} className="w-full h-12 rounded-xl text-base">
            {loading ? "Loading link…" : "Get confirmation link"}
          </Button>
        </div>
      );
    }
    return (
      <>
        <div className="flex items-start gap-3 rounded-2xl bg-secondary p-4">
          <ShieldCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <p className="text-sm text-muted-foreground">
            We'll create a secure private link so {debt?.person_name} can confirm the {amountStr} repayment — no Debii account needed.
          </p>
        </div>
        <Button data-testid="confirm-generate-button" disabled={loading} onClick={generate} className="w-full h-12 rounded-xl text-base">
          {loading ? "Creating link…" : "Create confirmation link"}
        </Button>
      </>
    );
  };

  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) setLink(null); onOpenChange(o); }}>
      <DrawerContent className="max-w-[30rem] mx-auto">
        <DrawerHeader className="text-left">
          <DrawerTitle className="font-display text-xl">{title}</DrawerTitle>
        </DrawerHeader>
        <div className="px-5 pb-8 space-y-4 overflow-y-auto overscroll-contain">{renderBody()}</div>
      </DrawerContent>
    </Drawer>
  );
}
